



















































import { Component, Watch, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { DataModules } from "@/store/modules/data";
import { DynamicSettingsFieldTypes as FieldTypes } from "@/data/enums/fields";

// Types
import { IState } from "@/store";
import { ICategoryGroup } from "@/models/settings";

@Component({
  components: {
    "dynamic-category-field": () =>
      import("@/components/app/admin/settings/dynamicCategoryField.vue")
  },
  computed: {
    ...mapState({
      categoryGroupsScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/categoryGroupsScope`](
          this.entity,
          this.code
        );
      },
      categoryGroups(state: IState, getters) {
        return getters[`data/list`]({
          storeModule: this.dataModule,
          scope: this.categoryGroupsScope
        });
      }
    })
  }
})
export default class DynamicSettings extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters
  public categoryGroupsScope!: string;
  public categoryGroups!: ICategoryGroup[];

  // Props
  @Prop({ default: "invoices" })
  public code!: string;

  @Prop({ default: "brand" })
  public entity!: string;

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public predefinedSettingsLoading!: boolean;

  // Variables
  public dataModule: string = DataModules.SETTINGS_CATEGORIES;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public contentIsAvailable =
    this.$userCan("get_brand") && this.$userCan("list_settings");
  public guardFunctionalities = ["get_brand", "list_settings"];
  public canManageBrand = this.$userCan("update_brand");

  public form = {};
  public formClone = {};

  public created() {
    if (this.contentIsAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      await this.getCategoryGroups();
      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getCategoryGroups() {
    return this.$store.dispatch(`data/${this.dataModule}/getCategoryGroups`, {
      entity: this.entity,
      code: this.code,
      scope: this.categoryGroupsScope,
      vm: this
    });
  }

  public fillForm() {
    const _ = this.$_;
    _.forEach(this.filteredCategoryGroups, group => {
      _.forEach(group.fields, field => {
        const type = field.field_type;
        let value = _.get(field, "value.value", field.default_value);
        if (type === FieldTypes.MULTIPLE && value === null) value = [];
        if (type === FieldTypes.NUMBER) value = value.toString();
        this.$set(
          this.form,
          `groups.${group.code}.fields.${field.code}`,
          value
        );
      });
    });
    this.formClone = _.cloneDeep(this.form);
  }

  public get filteredCategoryGroups() {
    return this.$_.omitBy(
      this.$_.map(
        this.$_.orderBy(this.categoryGroups, "order", "asc"),
        group => {
          group.fields = this.$_.orderBy(group.fields, "order", "asc");
          return group;
        }
      ),
      (group: ICategoryGroup) => this.$_.isEmpty(group.fields)
    );
  }

  public get isFieldDisabled() {
    return this.isProcessing || !this.canManageBrand;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  public isNum(value) {
    return !isNaN(value);
  }

  public async save() {
    return new Promise<void>((resolve, reject) => {
      this.updateSettings()
        .then(() => {
          this.formClone = this.$_.cloneDeep(this.form);
          resolve();
        })
        .catch(error => {
          this.$store.dispatch("api/handleValidationError", {
            error,
            vm: this,
            showToast: false
          });
          reject(error);
        });
    });
  }

  public revertToInitialState() {
    this.form = this.$_.cloneDeep(this.formClone);
  }

  public updateSettings() {
    return this.$store.dispatch(
      `data/${this.dataModule}/updateCategoryFields`,
      {
        code: this.code,
        entity: this.entity,
        scope: this.categoryGroupsScope,
        data: this.prepareResults
      }
    );
  }

  public get prepareResults() {
    return {
      groups: this.$_.mapValues(
        this.$_.keyBy(this.filteredCategoryGroups, "code"),
        group => {
          return {
            fields: this.$_.mapValues(
              this.$_.keyBy(group.fields, "code"),
              field => this.form[`groups.${group.code}.fields.${field.code}`]
            )
          };
        }
      )
    };
  }

  @Watch("formIsChanged")
  public onFormIsChanged() {
    this.$emit("dynamicSettingsAreChanged", this.formIsChanged);
  }

  @Watch("hasErrors")
  public onFormHasErrorChange() {
    this.$emit("dynamicSettingsHasErrors", this.hasErrors);
  }

  @Watch("isLoading")
  public onIsLoadingChange() {
    this.$emit("dynamicSettingsAreLoading", this.isLoading);
  }

  public beforeDestroy() {
    if (this.contentIsAvailable) {
      this.$store.dispatch("data/bin", {
        storeModule: this.dataModule,
        scope: this.categoryGroupsScope,
        vm: this
      });
    }
  }
}
